import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getAccessToken } from "@components/Utils/Utils.js";
import axios from "axios";
import { handleSessionExpiry } from "@/utils/http";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import Alerts from "@components/ui/Alerts";
import InvoiceIcon from "@/assets/icons/invoice-list-item.svg";
import CreditCardIcon from "@/assets/icons/credit-cards.svg";
import PaymentSignatureCanvas from "./PaymentSignatureCanvas";
import { buildSepaMandateData, normalizeText } from "@features/settings/services/PaymentSepaMandatePdf";
import { referralService } from "@/services/referralApi";
import "./Payment.css";
import "./Referral.css";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const MONTHLY_PRICE_PER_THERAPIST = 18.0;
const YEARLY_DISCOUNT = 0.1; // 10%

function PaymentMethodNew({ handleClose }) {
  const [paymentMethod, setPaymentMethod] = useState("invoice");
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [therapists, setTherapists] = useState(1);
  const [clientSecret, setClientSecret] = useState(null);
  const [referral, setReferral] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    referralService
      .getReferral()
      .then(setReferral)
      .catch((error) => console.error("Error fetching referral:", error));
  }, []);

  const monthlyTotal = therapists * MONTHLY_PRICE_PER_THERAPIST;
  const yearlyTotal = monthlyTotal * 12 * (1 - YEARLY_DISCOUNT);
  const yearlySavings = monthlyTotal * 12 - yearlyTotal;

  // Referral discount: a share of the first months of what is paid, by invoice
  // only. Computed like Referral.discount_for on the server, which the order
  // mail to the team uses.
  const referralPercent = referral
    ? Math.round(referral.discount_rate * 100)
    : 0;
  const referralMonthly = referral
    ? (billingPeriod === "yearly" ? yearlyTotal / 12 : monthlyTotal) *
      referral.discount_rate
    : 0;
  const referralTotal = referral
    ? referralMonthly * referral.discount_months
    : 0;

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    setClientSecret(null);
  };

  const handleBillingPeriodChange = (period) => {
    setBillingPeriod(period);
    setClientSecret(null);
  };

  const handleTherapistChange = (e) => {
    setTherapists(Number(e.target.value));
    setClientSecret(null);
  };

  return (
    <div className="payment-method-new">
      <div className="payment-method-header">
        <h2 className="payment-method-title">{t("payment.paymentMethod")}</h2>
        <p className="payment-method-subtitle">{t("payment.modalSubtitle")}</p>
      </div>

      {/* Payment Method Selection */}
      <div className="payment-method-selection">
        <div className="payment-method-tabs">
          <button
            type="button"
            className={`payment-tab ${paymentMethod === "invoice" ? "active" : ""}`}
            onClick={() => handlePaymentMethodChange("invoice")}
          >
            <img
              src={InvoiceIcon}
              alt="invoice"
              className="tab-icon"
              width={24}
              height={24}
            />
            <span className="tab-label">{t("payment.invoice")}</span>
          </button>
          <button
            type="button"
            className={`payment-tab ${paymentMethod === "credit" ? "active" : ""}`}
            onClick={() => handlePaymentMethodChange("credit")}
          >
            <img
              src={CreditCardIcon}
              alt="credit card"
              className="tab-icon"
              width={24}
              height={24}
            />
            <span className="tab-label">{t("payment.creditCard")}</span>
          </button>
        </div>
      </div>

      {/* Billing Period Selection (only for invoice) */}
      {paymentMethod === "invoice" && (
        <div className="billing-period-selection">
          <div className="billing-period-cards">
            <div
              className={`billing-card ${billingPeriod === "monthly" ? "selected" : ""}`}
              onClick={() => handleBillingPeriodChange("monthly")}
            >
              <div className="billing-card-header">
                <input
                  type="radio"
                  name="billingPeriod"
                  checked={billingPeriod === "monthly"}
                  onChange={() => handleBillingPeriodChange("monthly")}
                  className="billing-radio"
                />
                <span className="billing-label">{t("payment.monthly")}</span>
              </div>
              <div className="billing-price">
                {monthlyTotal.toFixed(2)} €
                <span className="billing-period-text">
                  {t("payment.perMonth")}
                </span>
                <div className="billing-vat">{t("payment.exclVat")}</div>
              </div>
            </div>
            <div
              className={`billing-card ${billingPeriod === "yearly" ? "selected" : ""} yearly-card`}
              onClick={() => handleBillingPeriodChange("yearly")}
            >
              <div className="billing-card-header">
                <input
                  type="radio"
                  name="billingPeriod"
                  checked={billingPeriod === "yearly"}
                  onChange={() => handleBillingPeriodChange("yearly")}
                  className="billing-radio"
                />
                <span className="billing-label">{t("payment.yearly")}</span>
                <span className="discount-badge">
                  {t("payment.discountBadge")}
                </span>
              </div>
              <div className="billing-price">
                {(yearlyTotal / 12).toFixed(2)} €
                <span className="billing-period-text">
                  {t("payment.perMonth")}
                </span>
              </div>
              <div className="billing-yearly-total">
                {yearlyTotal.toFixed(2)} € {t("payment.perYear")}
              </div>
              <div className="billing-vat">{t("payment.exclVat")}</div>
              <div className="billing-savings">
                {t("payment.youSave", { amount: yearlySavings.toFixed(2) })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Therapists Selection */}
      <div className="therapists-selection">
        <label className="therapists-label">
          {t("payment.therapistsCount")}
        </label>
        <div className="therapists-input-wrapper">
          <button
            type="button"
            className="therapists-btn therapists-btn-minus"
            onClick={() => setTherapists(Math.max(1, therapists - 1))}
            disabled={therapists <= 1}
          >
            −
          </button>
          <input
            type="number"
            className="therapists-input"
            value={therapists}
            onChange={handleTherapistChange}
            min="1"
            max="10"
            disabled={paymentMethod === "credit" && clientSecret !== null}
          />
          <button
            type="button"
            className="therapists-btn therapists-btn-plus"
            onClick={() => setTherapists(Math.min(10, therapists + 1))}
            disabled={therapists >= 10}
          >
            +
          </button>
        </div>
      </div>

      {/* Total Display */}
      <div className="payment-total">
        <div className="payment-total-label">{t("payment.totalPrice")}</div>
        <div className="payment-total-amount">
          {paymentMethod === "invoice" && billingPeriod === "yearly"
            ? `${yearlyTotal.toFixed(2)} € ${t("payment.perYear")}`
            : `${monthlyTotal.toFixed(2)} € ${t("payment.perMonth")}`}
        </div>
        <div className="payment-total-vat">({t("payment.exclVat")})</div>
        {paymentMethod === "invoice" && billingPeriod === "yearly" && (
          <div className="payment-total-savings">
            ({t("payment.youSave", { amount: yearlySavings.toFixed(2) })})
          </div>
        )}
        {referral?.discount_available && paymentMethod === "invoice" && (
          <div className="payment-total-referral">
            {billingPeriod === "yearly"
              ? t("referral.discountYearly", {
                  amount: referralTotal.toFixed(2),
                  percent: referralPercent,
                  months: referral.discount_months,
                })
              : t("referral.discountMonthly", {
                  amount: referralMonthly.toFixed(2),
                  percent: referralPercent,
                  months: referral.discount_months,
                })}
          </div>
        )}
        {referral?.discount_available && paymentMethod === "credit" && (
          <div className="payment-total-referral-hint">
            {t("referral.invoiceOnly", { percent: referralPercent })}
          </div>
        )}
      </div>

      {referral?.can_redeem && (
        <ReferralRedeem onRedeemed={setReferral} />
      )}

      {/* Form Content */}
      {paymentMethod === "invoice" ? (
        <InvoiceFormNew
          therapists={therapists}
          billingPeriod={billingPeriod}
          handleClose={handleClose}
        />
      ) : (
        <CreditCardFormNew
          therapists={therapists}
          setClientSecret={setClientSecret}
          clientSecret={clientSecret}
          handleClose={handleClose}
        />
      )}
    </div>
  );
}

// For practices that signed up without a code: it can still be entered
// before the first order.
function ReferralRedeem({ onRedeemed }) {
  const [code, setCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { t } = useTranslation();

  const handleRedeem = async () => {
    if (!code.trim()) return;
    setIsSubmitting(true);
    setError("");
    try {
      onRedeemed(await referralService.redeem(code.trim()));
    } catch (err) {
      setError(
        err.response?.data?.code === "REFERRAL_INVALID"
          ? t("referral.redeemInvalid")
          : t("referral.redeemError")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="form-group referral-redeem">
      <label className="form-label" htmlFor="referral-redeem-code">
        {t("referral.redeemLabel")}
      </label>
      <div className="referral-copy-row">
        <input
          id="referral-redeem-code"
          type="text"
          className="form-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="SOPHY-XXXXX"
          autoComplete="off"
        />
        <button
          type="button"
          className="payment-button payment-button-primary"
          onClick={handleRedeem}
          disabled={isSubmitting || !code.trim()}
        >
          {t("referral.redeem")}
        </button>
      </div>
      {error && <div className="referral-redeem-error">{error}</div>}
    </div>
  );
}

function InvoiceFormNew({ therapists, billingPeriod, handleClose }) {
  const [iban, setIban] = useState("");
  const [bankName, setBankName] = useState("");
  const [isSepaRecurring, setIsSepaRecurring] = useState(true);
  const [profileData, setProfileData] = useState({
    id: "",
    account_id: "",
    clinic_name: "",
    first_name: "",
    last_name: "",
    street: "",
    zip: "",
    city: "",
    country: "",
    email: "",
  });
  const [accountHolder, setAccountHolder] = useState("");
  const [mandateStreet, setMandateStreet] = useState("");
  const [mandateZip, setMandateZip] = useState("");
  const [mandateCity, setMandateCity] = useState("");
  const [signatureDataUrl, setSignatureDataUrl] = useState("");
  const [order, setOrder] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    const hasBaseFields =
      normalizeText(iban) && normalizeText(bankName) && order;
    const hasSepaFields =
      !isSepaRecurring ||
      (normalizeText(accountHolder) &&
        normalizeText(mandateStreet) &&
        normalizeText(mandateZip) &&
        normalizeText(mandateCity) &&
        signatureDataUrl);
    setIsFormValid(Boolean(hasBaseFields && hasSepaFields));
  }, [
    iban,
    bankName,
    order,
    isSepaRecurring,
    accountHolder,
    mandateStreet,
    mandateZip,
    mandateCity,
    signatureDataUrl,
  ]);

  useEffect(() => {
    const readProfile = async () => {
      const accessToken = getAccessToken();
      if (!accessToken) return;

      const url = `${
        process.env.REACT_APP_SOPHYAPP_API_URL
      }/de/react/${accessToken}/settings/profile`;

      try {
        const response = await axios.get(url, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
        const profile = response.data || {};
        setProfileData({
          id: profile.id || "",
          account_id: profile.account_id || profile.id || "",
          clinic_name: profile.clinic_name || "",
          first_name: profile.first_name || "",
          last_name: profile.last_name || "",
          street: profile.street || "",
          zip: profile.zip || "",
          city: profile.city || "",
          country: profile.country || "",
          email: profile.email || "",
        });
        setAccountHolder(
          normalizeText(
            `${profile.first_name || ""} ${profile.last_name || ""}`,
          ),
        );
        setMandateStreet(profile.street || "");
        setMandateZip(profile.zip || "");
        setMandateCity(profile.city || "");
      } catch (error) {
        console.error("Could not prefill profile for SEPA mandate:", error);
      }
    };

    readProfile();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid) return;

    setIsSubmitting(true);
    const url = `${
      process.env.REACT_APP_SOPHYAPP_API_URL
    }/de/react/${getAccessToken()}/settings/update_payment`;
    const params = new FormData();
    params.append("iban", iban);
    params.append("bankName", bankName);
    params.append("paymentMethod", "invoice");
    params.append("therapists", therapists);
    params.append("billingPeriod", billingPeriod);

    if (isSepaRecurring) {
      const sepaData = buildSepaMandateData({
        profileData,
        accountHolder,
        iban,
        bankName,
        mandateStreet,
        mandateZip,
        mandateCity,
      });

      params.append("paymentSubMethod", "sepa_recurring");
      params.append("sepaRecurring", "true");
      params.append("sepaSignatureDataUrl", signatureDataUrl);
      params.append("sepaMandateData", JSON.stringify(sepaData));
      // Explicit flags for backend workflow (e.g. internal review/forwarding)
      params.append("sepaMandateReviewState", "pending");
      params.append("sepaMandateNotifyInternal", "true");
    }

    try {
      await axios.post(url, params);
      setAlert({
        open: true,
        message: t("payment.successMessage"),
        severity: "success",
      });
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (error) {
      setAlert({
        open: true,
        message: t("payment.errorOccurred", { error: error.message }),
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="invoice-form-new" onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="form-label">{t("payment.iban")}</label>
        <input
          type="text"
          className="form-input"
          value={iban}
          onChange={(e) => setIban(e.target.value)}
          placeholder="DE89 3704 0044 0532 0130 00"
          required
        />
      </div>
      <div className="form-group">
        <label className="form-label">{t("payment.bankName")}</label>
        <input
          type="text"
          className="form-input"
          value={bankName}
          onChange={(e) => setBankName(e.target.value)}
          placeholder={t("payment.bankNamePlaceholder")}
          required
        />
      </div>
      <div className="form-checkbox-group">
        <label className="form-checkbox-label">
          <input
            type="checkbox"
            className="form-checkbox"
            checked={isSepaRecurring}
            onChange={(e) => setIsSepaRecurring(e.target.checked)}
          />
          <span>{t("payment.enableRecurringSepa")}</span>
        </label>
      </div>
      {isSepaRecurring && (
        <div className="payment-info-note">
          {t("payment.sepaRecurringInfo")}
        </div>
      )}
      {isSepaRecurring && (
        <div className="sepa-section">
          <div className="sepa-section-title">
            {t("payment.sepaMandateData")}
          </div>
          <div className="form-group">
            <label className="form-label">{t("payment.accountHolder")}</label>
            <input
              type="text"
              className="form-input"
              value={accountHolder}
              onChange={(e) => setAccountHolder(e.target.value)}
              placeholder={t("payment.accountHolderPlaceholder")}
              required={isSepaRecurring}
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t("misc.payment.streetLabel")}</label>
            <input
              type="text"
              className="form-input"
              value={mandateStreet}
              onChange={(e) => setMandateStreet(e.target.value)}
              placeholder={t("misc.payment.streetPlaceholder")}
              required={isSepaRecurring}
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t("misc.payment.zipLabel")}</label>
            <input
              type="text"
              className="form-input"
              value={mandateZip}
              onChange={(e) => setMandateZip(e.target.value)}
              placeholder="12345"
              required={isSepaRecurring}
            />
          </div>
          <div className="form-group">
            <label className="form-label">{t("payment.mandateCity")}</label>
            <input
              type="text"
              className="form-input"
              value={mandateCity}
              onChange={(e) => setMandateCity(e.target.value)}
              placeholder={t("payment.mandateCityPlaceholder")}
              required={isSepaRecurring}
            />
          </div>
          <div className="sepa-profile-preview">
            <div>
              {t("payment.mandatePrefillInfo")}{" "}
              <strong>
                {normalizeText(
                  `${profileData.first_name} ${profileData.last_name}`,
                ) || "-"}
              </strong>
              {profileData.clinic_name ? `, ${profileData.clinic_name}` : ""}
            </div>
            <div>{mandateStreet || "-"}</div>
            <div>
              {[mandateZip, mandateCity].filter(Boolean).join(" ") || "-"}
            </div>
            <div>{profileData.email || "-"}</div>
          </div>
          <div className="form-group">
            <label className="form-label">{t("payment.signature")}</label>
            <PaymentSignatureCanvas onSignatureChange={setSignatureDataUrl} />
          </div>
        </div>
      )}
      <div className="form-checkbox-group">
        <label className="form-checkbox-label">
          <input
            type="checkbox"
            className="form-checkbox"
            checked={order}
            onChange={(e) => setOrder(e.target.checked)}
            required
          />
          <span>{t("payment.orderCharged")}</span>
        </label>
      </div>
      <button
        type="submit"
        className="payment-submit-btn"
        disabled={!isFormValid || isSubmitting}
      >
        {isSubmitting ? (
          <span className="payment-submit-content">
            <span className="payment-submit-spinner" />
            {t("payment.loading")}
          </span>
        ) : (
          t("payment.submit")
        )}
      </button>
      <Alerts
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() =>
          setAlert({ open: false, message: "", severity: "success" })
        }
      />
    </form>
  );
}

function CreditCardFormNew({
  therapists,
  setClientSecret,
  clientSecret,
  handleClose,
}) {
  const [isLoadingIntent, setIsLoadingIntent] = useState(false);
  const [localClientSecret, setLocalClientSecret] = useState(null);
  const { t } = useTranslation();

  const handleNext = async (e) => {
    e.preventDefault();
    setIsLoadingIntent(true);
    try {
      const urlPaymentIntent = `${
        process.env.REACT_APP_SOPHYAPP_API_URL
      }/de/react/${getAccessToken()}/settings/create_payment_intent`;
      const response = await axios.post(urlPaymentIntent, { therapists });
      if (response.data.clientSecret) {
        setLocalClientSecret(response.data.clientSecret);
        setClientSecret(response.data.clientSecret);
      } else {
        console.error("Failed to fetch client secret");
      }
    } catch (error) {
      console.error("Error fetching client secret:", error);
    } finally {
      setIsLoadingIntent(false);
    }
  };

  return (
    <div className="credit-card-form-new">
      {!localClientSecret ? (
        <button
          type="button"
          className="payment-submit-btn"
          onClick={handleNext}
          disabled={isLoadingIntent}
        >
          {isLoadingIntent ? t("payment.loading") : t("payment.next")}
        </button>
      ) : (
        <Elements
          stripe={stripePromise}
          options={{ clientSecret: localClientSecret }}
        >
          <StripePaymentFormNew
            clientSecret={localClientSecret}
            handleClose={handleClose}
            therapists={therapists}
          />
        </Elements>
      )}
    </div>
  );
}

function StripePaymentFormNew({ clientSecret, handleClose, therapists }) {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);

  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handlePaymentChange = (event) => {
    setPaymentComplete(event.complete);
  };

  const handleStripePayment = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || !clientSecret) {
      console.error("Stripe.js has not loaded yet.");
      return;
    }

    setIsProcessing(true);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        redirect: "if_required",
      });

      if (error) {
        console.error(error);
        setAlert({
          open: true,
          message: t("payment.paymentFailed", { error: error.message }),
          severity: "error",
        });
      } else {
        const urlDefaultMethod = `${
          process.env.REACT_APP_SOPHYAPP_API_URL
        }/de/react/${getAccessToken()}/settings/set_default_payment_method`;
        const response = await fetch(urlDefaultMethod, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            paymentMethodId: paymentIntent.payment_method,
          }),
        });

        if (response.status === 401) {
          handleSessionExpiry();
          return;
        }
        if (response.ok) {
          setAlert({
            open: true,
            message: t("payment.paymentSuccess"),
            severity: "success",
          });
          setTimeout(() => {
            handleClose();
          }, 1500);
        } else {
          setAlert({
            open: true,
            message: t("payment.paymentSuccessPartial"),
            severity: "error",
          });
          setTimeout(() => {
            handleClose();
          }, 1500);
        }
      }
    } catch (error) {
      console.error("Error processing payment:", error);
      setAlert({
        open: true,
        message: t("payment.errorOccurred", { error: error.message }),
        severity: "error",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="stripe-payment-form-new">
      <div className="stripe-payment-element">
        <PaymentElement onChange={handlePaymentChange} />
      </div>
      <button
        type="submit"
        className="payment-submit-btn"
        disabled={!stripe || !elements || isProcessing || !paymentComplete}
        onClick={handleStripePayment}
      >
        {isProcessing ? t("payment.processing") : t("payment.submit")}
      </button>
      <Alerts
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={() =>
          setAlert({ open: false, message: "", severity: "success" })
        }
      />
    </div>
  );
}

export default PaymentMethodNew;
